import { useQuery } from '@tanstack/react-query';
import { MentalHealthReport } from '@/util/bcg/types';
import { mentalHealthStore } from '@/store/MentalHealthStore';

export interface MentalHealthReportListItem {
  id: number | string;
  userId: number;
  userName: string;
  filename: string;
  analysisDate: string;
  mindScore: number;
  decisionLabel: string;
  stressCategory: string;
  duration: number;
  quality: number;
}

export interface GetMentalHealthReportsRequest {
  userId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface GetMentalHealthReportsResponse {
  reports: MentalHealthReportListItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * 마음 건강 리포트 목록 조회 API (목 데이터)
 */
async function getMentalHealthReports(
  params: GetMentalHealthReportsRequest = {}
): Promise<GetMentalHealthReportsResponse> {
  // 저장된 분석 결과에서 리포트 목록 생성
  return new Promise((resolve) => {
    setTimeout(() => {
      const allReports = mentalHealthStore.getAllReports();

      // 필터링
      let filteredReports = allReports;

      if (params.userId) {
        filteredReports = filteredReports.filter(r => r.userId === params.userId);
      }

      if (params.startDate) {
        const startDate = new Date(params.startDate);
        filteredReports = filteredReports.filter(r => new Date(r.analysisDate) >= startDate);
      }

      if (params.endDate) {
        const endDate = new Date(params.endDate);
        endDate.setHours(23, 59, 59, 999); // 해당 날짜 끝까지
        filteredReports = filteredReports.filter(r => new Date(r.analysisDate) <= endDate);
      }

      // 페이지네이션
      const page = params.page || 1;
      const limit = params.limit || 20;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;

      const paginatedReports = filteredReports.slice(startIndex, endIndex);

      // 응답 형식에 맞게 변환
      const reports: MentalHealthReportListItem[] = paginatedReports.map(stored => ({
        id: stored.id,
        userId: stored.userId,
        userName: stored.userName,
        filename: stored.filename,
        analysisDate: stored.analysisDate,
        mindScore: stored.report.mindScore.score,
        decisionLabel: stored.report.decisionLabel,
        stressCategory: stored.report.stressAnalysis.category,
        duration: stored.report.qualityMetrics.duration_min,
        quality: Math.round(stored.report.qualityMetrics.analyzability * 100)
      }));

      resolve({
        reports,
        total: filteredReports.length,
        page,
        limit
      });
    }, 300);
  });
}

/**
 * 마음 건강 리포트 목록 조회 Hook
 */
export function useQueryGetMentalHealthReports(params: GetMentalHealthReportsRequest = {}) {
  return useQuery({
    queryKey: ['mentalHealth', 'reports', params],
    queryFn: () => getMentalHealthReports(params),
  });
}

/**
 * 특정 리포트 상세 조회 API (목 데이터)
 */
export interface MentalHealthReportDetailResponse {
  report: MentalHealthReport;
  userId: number;
  userName: string;
  analysisDate: string;
}

/**
 * 특정 리포트 상세 조회 API (목 데이터)
 */
async function getMentalHealthReportDetail(reportId: number | string): Promise<MentalHealthReportDetailResponse | null> {
  // 저장된 분석 결과에서 상세 정보 조회
  return new Promise((resolve) => {
    setTimeout(() => {
      const storedReport = mentalHealthStore.getReportById(reportId);
      if (storedReport) {
        resolve({
          report: storedReport.report,
          userId: storedReport.userId,
          userName: storedReport.userName,
          analysisDate: storedReport.analysisDate
        });
      } else {
        resolve(null);
      }
    }, 300);
  });
}

/**
 * 특정 리포트 상세 조회 Hook
 */
export function useQueryGetMentalHealthReportDetail(reportId?: number | string) {
  return useQuery({
    queryKey: ['mentalHealth', 'reports', 'detail', reportId],
    queryFn: () => getMentalHealthReportDetail(reportId!),
    enabled: !!reportId,
  });
}
